import { useUser } from '@/queries/useUser';
import { FC, useEffect, useMemo, useRef } from 'react';

import { useCustomizationStore } from '../../stores/customizationStore';
import { PreviewCommands } from './constants';
import { useIframe } from './hooks';

interface Props {
    src: string;
    className?: string;
    editBlockId?: number | string | null; // Set focus in an specific block
    errorBlockId?: number | string | null; // Set error in an specific block
    editMode?: boolean; // Enable edit mode. i.e show edit buttons and focus on the block
    onLoaded?: () => void; // Callback when the preview is loaded
    onEditBlock?: (id: number | string | null) => void; // Callback when an block is edited
    onAddBlock?: (id: number | string | null, order: number) => void; // Callback when an block is added
}

export const Preview: FC<Props> = ({
    src,
    className,
    editBlockId,
    errorBlockId,
    editMode,
    onLoaded,
    onEditBlock,
    onAddBlock,
}) => {
    const iframeRef = useRef<HTMLIFrameElement | null>(null);
    const { theme, colors, primaryFont, secondaryFont, imageFrames, storeItems, header } = useCustomizationStore();
    const { user } = useUser();
    /** Prepare initial data to send to iframe on initialization */
    const syncData = useMemo(
        () => ({
            storeItems: storeItems ?? [],
            header: header ?? undefined,
            colors: colors ?? undefined,
            fonts: {
                primary: primaryFont ?? undefined,
                secondary: secondaryFont ?? undefined,
            },
            imageFrames: imageFrames ?? undefined,
            theme: theme ?? undefined,
            user: user ?? undefined,
        }),
        [storeItems, header, colors, primaryFont, secondaryFont, imageFrames, theme, user]
    );

    /** Sets up iframe communication and handles incoming messages */
    const { isReady, sendMessage } = useIframe({
        iframeRef,
        initialData: syncData,
        onMessage: event => {
            if (event.type === PreviewCommands.Ready) onLoaded?.();
            else if (event.type === PreviewCommands.EditBlock) onEditBlock?.(event.payload.id);
            else if (event.type === PreviewCommands.AddBlock) onAddBlock?.(event.payload.id, event.payload.order);
        },
    });

    /** Syncs full state to iframe whenever any relevant state changes */
    useEffect(() => {
        sendMessage(PreviewCommands.SyncState, syncData);
    }, [syncData, sendMessage]);

    /** Toggles edit mode in iframe */
    useEffect(() => {
        if (!isReady) return;
        sendMessage(PreviewCommands.ToggleEditMode, {
            edit: editMode ?? false,
        });
    }, [editMode, sendMessage, isReady]);

    /** Focuses a specific item in iframe */
    useEffect(() => {
        if (!isReady) return;
        sendMessage(PreviewCommands.FocusBlock, {
            id: editBlockId ?? null,
        });
    }, [editBlockId, sendMessage, isReady]);

    /** Sets error state on a specific item in iframe */
    useEffect(() => {
        if (!isReady) return;
        sendMessage(PreviewCommands.ErrorBlock, {
            id: errorBlockId ?? null,
        });
    }, [errorBlockId, sendMessage, isReady]);

    return (
        <div
            className='data-[state=show]:animate-in fade-in fill-mode-backwards flex h-full w-full flex-1 items-center justify-center delay-200 duration-600'
            data-state={isReady && 'show'}
        >
            <iframe src={src} className={className} ref={iframeRef} />
        </div>
    );
};
